/**
 * De bouwnummerfilter bovenin een scherm: één knop per bouwnummer, met de bootkleur.
 *
 * Vervangt de kiezer die in de zijbalk stond. Het gekozen bouwnummer geldt voor de
 * hele app (`kiesBoot`): wie op het dashboard 1014 kiest, ziet in de houtstaat en op
 * de werkvloer ook 1014. Met `metAlle` staat er vooraan "Alle" bij; de planning heeft
 * dat, omdat de machines voor alle boten tegelijk werken. Dan beslist het scherm zelf
 * wat de keuze doet, via `waarde` en `onKies`.
 *
 * Een radiogroep: met Tab kom je op het gekozen bouwnummer, met de pijltjes kies je
 * het vorige of volgende.
 */
import { useRef, type CSSProperties, type KeyboardEvent } from 'react';

import { bouwnummerKort, useGegevens } from '../gegevens';

interface Props {
  /** Alleen met `metAlle`: het gekozen nummer, of `null` voor alle bouwnummers. */
  waarde?: string | null;
  onKies?: (bouwnummer: string | null) => void;
  metAlle?: boolean;
  /** Een getal achter elk nummer, bijvoorbeeld hoeveel karren er nog in te plannen zijn. */
  teller?: (bouwnummer: string | null) => number | undefined;
}

export function Bouwnummerfilter({ waarde, onKies, metAlle = false, teller }: Props) {
  const { bouwnummers, boot, kiesBoot } = useGegevens();
  const groep = useRef<HTMLDivElement>(null);
  const gekozen = waarde !== undefined ? waarde : (boot?.bouwnummer ?? null);
  const opties: Array<string | null> = [...(metAlle ? [null] : []), ...bouwnummers.map((b) => b.bouwnummer)];

  const kies = (nr: string | null) => {
    if (onKies) onKies(nr);
    else if (nr) kiesBoot(nr);
  };

  const opToets = (e: KeyboardEvent<HTMLDivElement>) => {
    const stap = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!stap) return;
    e.preventDefault();
    const nu = opties.indexOf(gekozen);
    const volgende = opties[(nu + stap + opties.length) % opties.length] ?? null;
    kies(volgende);
    // De focus gaat mee naar de nieuwe keuze, zoals in een radiogroep.
    requestAnimationFrame(() => groep.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus());
  };

  if (bouwnummers.length === 0) return null;

  return (
    <div className="hs-bootfilter" role="radiogroup" aria-label="Bouwnummer" ref={groep} onKeyDown={opToets}>
      <span className="hs-annotatie hs-bootfilter__label" aria-hidden>
        Bouwnummer
      </span>
      {opties.map((nr) => {
        const b = nr ? bouwnummers.find((x) => x.bouwnummer === nr) : null;
        const aan = nr === gekozen;
        const getal = teller?.(nr);
        return (
          <button
            key={nr ?? 'alle'}
            type="button"
            role="radio"
            aria-checked={aan}
            tabIndex={aan ? 0 : -1}
            className="hs-bootfilter__optie"
            style={b ? ({ '--boot': b.kleur } as CSSProperties) : undefined}
            title={b ? `${b.bouwnummer} · ${b.omschrijving} · v${b.versie} · rev ${b.revisie}` : 'Alle bouwnummers'}
            onClick={() => kies(nr)}
          >
            {b ? <span className="hs-bootfilter__stip" aria-hidden /> : null}
            <span>{nr ? bouwnummerKort(nr) : 'Alle'}</span>
            {getal != null ? <span className="hs-bootfilter__teller">{getal}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
